import React, { useState, useEffect, useCallback } from 'react';
import { estRubrique, libelleRubrique } from '../services/jdRubrique';
import { alertErpia, confirmErpia } from './ConfirmDialog';
import APIService from '../services/api';
import { GrilleBesoin } from './GrilleAdjudication';

// La date de démarrage saisie sur le besoin (« 2026-12-01 », ISO ou non),
// écrite comme on la lit en Suisse romande. Le salaire max n'apparaît
// jamais ici : la fiche de poste part chez des candidats et des partenaires.
const dateDemarrageCH = (v) => { const m = String(v || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}.${m[2]}.${m[1]}` : null; };

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style, className }) => <i className={`bi bi-${icon}${className ? ` ${className}` : ''}`} style={style} aria-hidden="true" />;

const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });
const splitSkills = (v) => Array.isArray(v) ? v.filter(Boolean) : String(v || '').split(/[,;/•\n]+/).map(s => s.trim()).filter(Boolean);
const fmtDate = (d) => { if (!d) return '—'; const t = new Date(d); return isNaN(t) ? '—' : t.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' }); };
const toLines = (v) => (Array.isArray(v) ? v : String(v || '').split(/\r?\n/)).map(s => String(s).trim()).filter(Boolean);

/**
 * Fiche de poste (JD) — document structuré (contrat v29).
 * SOURCE DE VÉRITÉ : GET /besoins/:id/jd → { contexte, missions[], profil[],
 * status: 'draft'|'validated', updated_at } (auto-créée, jamais 404 si le
 * besoin existe). Édition : PUT /besoins/:id/jd avec les champs modifiés
 * (status inclus — 'validated' pour le chip vert). Le PDF (GET /besoins/:id/jd.pdf)
 * rend la même structure : le document affiché EST le document envoyé.
 *
 * LEMAN RÉDIGE, L'ÉQUIPE VALIDE (13.10). « Rédiger avec Leman » propose les
 * fiches validées qui peuvent servir de modèle (GET /besoins/:id/jd/modeles,
 * même client d'abord), puis écrit un brouillon (POST /besoins/:id/jd/rediger).
 * La fiche lue porte sa provenance (`redige_par_ia`, `modele`), ce que Leman
 * n'a pas pu savoir (`a_verifier`), des contrôles par RÈGLES (`controles`) et
 * qui l'a validée (`validation`). Tant qu'un brouillon de Leman n'est pas
 * validé, rien ne part : le serveur retient sourcing, annonce et LinkedIn
 * (`en_attente_validation`) — l'écran le DIT, il ne le décide pas.
 */
export default function JDDocument({ besoin, showNotification, onGeneratePDF, onSourcingAuto }) {
  const b = besoin || {};
  const [jd, setJd] = useState(null);       // { contexte, missions[], profil[], status, updated_at }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ contexte: '', missions: '', profil: '' });
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [modified, setModified] = useState(false);
  const [genPdf, setGenPdf] = useState(false);
  // « Rédiger avec Leman » : le panneau, les modèles proposés par le serveur
  // (null = chargement), le choix (id d'un modèle, ou 'aucun') et les consignes.
  // UN panneau ouvert à la fois sous l'en-tête : 'leman' | 'mail' | 'partage' | null.
  // Trois tiroirs empilés repoussaient la fiche hors de l'écran.
  const [panneau, setPanneau] = useState(null);
  const basculer = (p) => setPanneau(x => (x === p ? null : p));
  const lemanOpen = panneau === 'leman';
  const [modeles, setModeles] = useState(null);
  const [choix, setChoix] = useState('aucun');
  const [consignes, setConsignes] = useState('');
  // Par défaut, la fiche ne nomme NI le client NI son secteur (elle part chez
  // des candidats, sur LinkedIn) : le nommer est une demande expresse.
  const [avecClient, setAvecClient] = useState(false);
  const [redaction, setRedaction] = useState(false);
  // Partage public de la JD : POST/GET /besoins/:id/jd/share (+ DELETE /share/:token).
  const shareOpen = panneau === 'partage';
  const [shares, setShares] = useState(null); // null = chargement
  const [shareAnon, setShareAnon] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  // ENVOYER LA FICHE PAR MAIL (18.09). Le lien existait, il fallait le copier
  // et ouvrir sa messagerie. Le formulaire est FERMÉ par défaut : on ne
  // demande « à qui ? » qu'après avoir décidé d'envoyer.
  const mailOpen = panneau === 'mail';
  const [mailTo, setMailTo] = useState('');
  const [mailMot, setMailMot] = useState('');
  // ANONYME par défaut, comme le serveur : le nom du client ne part pas sans
  // qu'on l'ait décidé — c'est ce qu'on ne rattrape pas une fois le mail parti.
  const [mailAnon, setMailAnon] = useState(true);
  const [mailPdf, setMailPdf] = useState(true);
  const [mailBusy, setMailBusy] = useState(false);
  const envoyerParMail = async () => {
    const dest = mailTo.trim();
    if (!dest) return;
    setMailBusy(true);
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd/envoyer`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ destinataires: dest, message: mailMot.trim() || null,
          anonyme: mailAnon, joindre_pdf: mailPdf }),
      });
      const j = await r.json().catch(() => ({}));
      // Un 404/501 MUET veut dire « la route n'existe pas encore » ; un 404
      // qui porte notre JSON est un vrai refus, qu'on affiche tel quel.
      if (r.status === 501 || (r.status === 404 && j.success !== false)) {
        showNotification?.('L\'envoi par mail arrive côté serveur — en attendant, copiez le lien de partage.', 'error');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message, 'success');
      setPanneau(null); setMailTo(''); setMailMot('');
    } catch (e) { showNotification?.(e.message, 'error'); } finally { setMailBusy(false); }
  };

  // IMPORTER UNE FICHE DE POSTE (11.09) : déposer une annonce PDF/Word
  // REMPLACE la fiche affichée. C'est destructif — ce qu'un recruteur a écrit
  // à la main disparaît — donc on demande, et le serveur rend l'ANCIENNE
  // version pour qu'un import raté se rattrape sans sauvegarde.
  const [important, setImportant] = useState(false);
  const [annule, setAnnule] = useState(null); // la JD d'avant ({ ..., par: 'import'|'leman' }), tant qu'on peut revenir
  const fichierRef = React.useRef(null);
  // La grille d'adjudication lue dans le document importé (souvent en
  // dernière page) : l'éditeur s'ouvre juste après l'import, pour validation.
  const [grilleOuvrir, setGrilleOuvrir] = useState(0);

  const importer = async (file) => {
    if (!file) return;
    const ok = await confirmErpia({
      title: 'Remplacer la fiche de poste ?',
      message: `« ${file.name} » va remplacer le contexte, les missions et le profil affichés. `
        + `Les questions d'entretien tirées de cette fiche seront régénérées. Vous pourrez revenir en arrière juste après.`,
      confirmLabel: 'Importer et remplacer', tone: 'danger',
    });
    if (!ok) return;
    setImportant(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      const r = await fetch(`${BASE}/besoins/${b.id}/jd/import`, { method: 'POST', headers: authH(), body: fd });
      if (r.status === 404 || r.status === 501) {
        showNotification?.("L'import de fiche de poste arrive côté serveur — rien n'a été remplacé.", 'info');
        return;
      }
      const j = await r.json().catch(() => ({}));
      // Le message du serveur fait foi : « document scanné en image », « format
      // non pris en charge » — il dit quoi faire, on ne le reformule pas.
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const d = j.data || {};
      setAnnule(d.avant ? { ...d.avant, par: 'import' } : null);
      await load();
      showNotification?.(`Fiche de poste remplacée depuis « ${file.name} ».`, 'success');
      // Une grille d'adjudication lue dans le document : à valider, tout de suite.
      if (d.grille) setGrilleOuvrir(n => n + 1);
      onSourcingAuto?.();
    } catch (e) { alertErpia({ title: 'Import impossible', message: e.message, tone: 'danger' }); }
    finally { setImportant(false); }
  };

  // Revenir à la version d'avant : le serveur nous l'a rendue, on la réécrit
  // par le même chemin que l'édition.
  const revenir = async () => {
    if (!annule) return;
    setImportant(true);
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd`, { method: 'PUT', headers: jsonH(),
        // version_precedente : un texte rétabli est celui d'une personne — la
        // marque « rédigé par Leman » du brouillon remplacé ne le suit pas.
        body: JSON.stringify({ contexte: annule.contexte, missions: annule.missions, profil: annule.profil, version_precedente: true }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setAnnule(null);
      await load();
      showNotification?.('Fiche de poste rétablie.', 'success');
    } catch (e) { alertErpia({ title: 'Retour impossible', message: e.message, tone: 'danger' }); }
    finally { setImportant(false); }
  };

  const load = useCallback(async () => {
    if (b.id == null) return;
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd`, { headers: authH() });
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        const d = j.data || j;
        if (d && typeof d === 'object') setJd({
          contexte: d.contexte || '', missions: toLines(d.missions), profil: toLines(d.profil), status: d.status || 'draft', updated_at: d.updated_at,
          // Provenance, relecture et validation : servies par le serveur, lues telles quelles.
          redige_par_ia: !!(d.redige_par_ia || d.source), source: d.source || null, modele: d.modele || null,
          a_verifier: Array.isArray(d.a_verifier) ? d.a_verifier : [],
          anonyme: d.anonyme === true,
          controles: d.controles && Array.isArray(d.controles.points) ? d.controles : null,
          validation: d.validation || null,
          en_attente: Array.isArray(d.en_attente_validation) ? d.en_attente_validation : [],
        });
      }
    } catch { /* offline → feuille dérivée du besoin en repli */ }
    finally { setLoading(false); }
  }, [b.id]);
  useEffect(() => { setEditing(false); setModified(false); load(); }, [load]);

  // Repli sans réseau : contenu dérivé du besoin — jamais de page vide.
  const eff = jd || {
    contexte: `${b.client ? `${b.client} ` : ''}recherche un(e) ${b.titre || 'profil'}${b.ville ? ` à ${b.ville}` : ''}. Cette mission s'inscrit dans le cadre du développement des équipes et des projets en cours.`,
    missions: ['Prendre en charge les responsabilités du poste.', 'Collaborer avec les équipes internes et le client.', 'Assurer la qualité et le respect des délais des livrables.'],
    profil: splitSkills(b.competences || b.skills).slice(0, 6).map(s => `Maîtrise de ${s}`),
    status: 'draft',
  };
  const validated = eff.status === 'validated';
  const vide = !!jd && !String(jd.contexte || '').trim() && !(jd.missions || []).length && !(jd.profil || []).length;
  const brouillonLeman = !!jd?.redige_par_ia && !validated;
  const points = jd?.controles?.points || [];
  const aRelire = points.filter(p => !p.ok);
  const sansRemarque = points.filter(p => p.ok && p.niveau !== 'info');
  const infos = points.filter(p => p.ok && p.niveau === 'info');
  // L'état de la fiche, tel qu'on le lit en tête (null tant qu'elle n'est pas lue).
  const etat = !jd ? null
    : vide ? { cle: 'vide', libelle: 'Pas encore de fiche' }
      : validated ? { cle: 'validee', libelle: 'Validée', detail: [jd.validation?.par && `par ${jd.validation.par}`, jd.validation?.le && `le ${fmtDate(jd.validation.le)}`].filter(Boolean).join(' · ') || null }
        : brouillonLeman ? { cle: 'brouillon', libelle: 'À valider' }
          : { cle: 'brouillon', libelle: 'Brouillon', detail: 'pas encore validée' };
  const skills = splitSkills(b.competences || b.skills);
  const meta = [
    ['Contrat', b.contrat || b.type_contrat || 'Mission'],
    ['Séniorité', b.seniorite || b.niveau || b.experience || '—'],
    [b.tjm || b.tarif ? 'Tarif' : 'Salaire', b.tjm || b.tarif || b.salaire || b.budget || '—'],
    ['Démarrage', dateDemarrageCH(b.date_demarrage) || b.demarrage || b.disponibilite || 'Dès que possible'],
    // Le lieu du besoin (14.10 : « il manque la localisation ») — c'est lui
    // que reprennent l'annonce et l'offre LinkedIn.
    ['Lieu', b.localisation || b.lieu || b.ville || '—'],
  ];

  const startEdit = () => { setDraft({ contexte: eff.contexte || '', missions: (eff.missions || []).join('\n'), profil: (eff.profil || []).join('\n') }); setEditing(true); };

  const save = async () => {
    setSaving(true);
    try {
      // On n'envoie QUE les champs modifiés (contrat : PUT partiel).
      const body = {};
      if (draft.contexte !== (eff.contexte || '')) body.contexte = draft.contexte;
      const dm = toLines(draft.missions), dp = toLines(draft.profil);
      if (dm.join('\n') !== (eff.missions || []).join('\n')) body.missions = dm;
      if (dp.join('\n') !== (eff.profil || []).join('\n')) body.profil = dp;
      if (Object.keys(body).length === 0) { setEditing(false); return; }
      const r = await fetch(`${BASE}/besoins/${b.id}/jd`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setModified(true); setEditing(false);
      // Une fiche validée qu'on modifie redevient un brouillon (serveur) :
      // ce qu'on valide, c'est un texte, pas une ligne.
      showNotification?.(validated ? 'Fiche de poste enregistrée — modifiée, elle repasse en brouillon : validez-la à nouveau.' : 'Fiche de poste enregistrée', 'success');
      // Compléter le JD peut relancer le sourcing automatique : la réponse
      // porte alors sourcing_auto — on le remonte pour que la pastille du
      // besoin change tout de suite, sans attendre le prochain rechargement.
      const sa = j.sourcing_auto ?? j.data?.sourcing_auto;
      if (sa !== undefined) onSourcingAuto?.(sa);
      load(); // l'état (status/updated_at) revient du serveur
    } catch (e) { showNotification?.(`Échec enregistrement : ${e.message}`, 'error'); } finally { setSaving(false); }
  };

  // Valider la JD (chip vert) — alimente l'item « JD validée » de Prêt à envoyer.
  const validate = async () => {
    setValidating(true);
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ status: 'validated' }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setJd(prev => prev ? { ...prev, status: 'validated' } : prev);
      showNotification?.(brouillonLeman ? 'Fiche de poste validée — elle peut partir.' : 'Fiche de poste validée', 'success');
      // Ce qui attendait la validation est parti (offre LinkedIn demandée sur
      // le brouillon) : le serveur dit ce qu'il en est, on le relaie tel quel.
      const li = (j.apres_validation ?? j.data?.apres_validation)?.linkedin;
      if (li) showNotification?.(li.ok ? li.message : li.error, li.ok ? 'success' : 'error');
      const sa = j.sourcing_auto ?? j.data?.sourcing_auto;
      if (sa !== undefined) onSourcingAuto?.(sa);
      load();
    } catch (e) { showNotification?.(`Échec validation : ${e.message}`, 'error'); } finally { setValidating(false); }
  };

  // ── Rédiger avec Leman ───────────────────────────────────────────────────
  // Les modèles se lisent à l'ouverture du panneau : le serveur les classe
  // (même client d'abord, puis l'intitulé le plus proche) — le premier est
  // présélectionné, « sans modèle » quand il n'y en a aucun.
  useEffect(() => {
    if (!lemanOpen || b.id == null) return undefined;
    let vivant = true;
    setModeles(null);
    (async () => {
      try {
        const r = await fetch(`${BASE}/besoins/${b.id}/jd/modeles`, { headers: authH() });
        const j = await r.json().catch(() => ({}));
        if (!vivant) return;
        if (r.status === 501 || (r.status === 404 && j.success !== false)) { setModeles({ absent: true }); return; }
        if (!r.ok || j.success === false) { setModeles({ erreur: j.error || `HTTP ${r.status}` }); return; }
        const liste = Array.isArray(j.data) ? j.data : [];
        setModeles(liste);
        setChoix(liste[0]?.id ?? 'aucun');
      } catch (e) { if (vivant) setModeles({ erreur: e.message }); }
    })();
    return () => { vivant = false; };
  }, [lemanOpen, b.id]);

  const rediger = async (remplacer = false) => {
    setRedaction(true);
    // La version affichée, gardée pour « Revenir à l'ancienne » si on remplace.
    const avant = jd && !vide ? { contexte: jd.contexte, missions: jd.missions, profil: jd.profil, par: 'leman' } : null;
    try {
      const corps = { ...(choix === 'aucun' ? { sans_modele: true } : { modele_id: choix }), remplacer, ...(avecClient ? { avec_client: true } : {}),
        ...(consignes.trim() ? { consignes: consignes.trim() } : {}) };
      const r = await fetch(`${BASE}/besoins/${b.id}/jd/rediger`, { method: 'POST', headers: jsonH(), body: JSON.stringify(corps) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || (r.status === 404 && j.success !== false)) {
        showNotification?.("La rédaction par Leman arrive côté serveur — rien n'a été écrit.", 'info');
        return;
      }
      // Une fiche existe déjà : le serveur ne l'écrase pas sans qu'on le dise.
      if (r.status === 409 && j.code === 'jd_existe') {
        const ok = await confirmErpia({
          title: 'Remplacer la fiche de poste ?',
          message: `${j.error} Le contexte, les missions et le profil affichés seront remplacés par un brouillon à relire. Vous pourrez revenir en arrière juste après.`,
          confirmLabel: 'Rédiger et remplacer', tone: 'danger',
        });
        if (ok) { setRedaction(false); await rediger(true); }
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      if (remplacer && avant) setAnnule(avant);
      setPanneau(null); setConsignes(''); setAvecClient(false);
      setModified(true);
      await load();
      showNotification?.(j.message || 'Brouillon de fiche de poste rédigé : relisez-le, puis validez-le.', 'success');
      onSourcingAuto?.();
    } catch (e) { alertErpia({ title: 'Rédaction impossible', message: e.message, tone: 'danger' }); }
    finally { setRedaction(false); }
  };

  const shareUrlOf = (s) => s.url || s.share_url || (s.token ? `${window.location.origin}/jd/${s.token}` : '');
  const loadShares = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd/share`, { headers: authH() });
      if (!r.ok) { setShares([]); return; }
      const j = await r.json().catch(() => ({}));
      const arr = j.data || j.shares || (Array.isArray(j) ? j : []);
      setShares(Array.isArray(arr) ? arr : []);
    } catch { setShares([]); }
  }, [b.id]);
  useEffect(() => { if (shareOpen) { setShares(null); loadShares(); } }, [shareOpen, loadShares]);

  const copyLink = async (u) => {
    try { await navigator.clipboard.writeText(u); showNotification?.('Lien copié.', 'success'); }
    catch { await alertErpia({ title: 'Copier le lien', message: 'Le presse-papiers est indisponible. Sélectionnez puis copiez le lien ci-dessous.', detail: u }); }
  };
  const createShare = async () => {
    setShareBusy(true);
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd/share`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ anonyme: shareAnon }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const u = shareUrlOf(j.data || j);
      if (u) copyLink(u);
      showNotification?.(j.message || `Lien ${shareAnon ? 'anonyme ' : ''}créé${u ? ' et copié' : ''}.`, 'success');
      loadShares();
    } catch (e) { showNotification?.('Erreur partage : ' + e.message, 'error'); }
    finally { setShareBusy(false); }
  };
  const revokeShare = async (s) => {
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd/share/${s.token}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Lien révoqué.', 'success');
      loadShares();
    } catch (e) { showNotification?.('Erreur révocation : ' + e.message, 'error'); }
  };

  const generatePdf = async () => {
    if (b.id == null) return onGeneratePDF?.();
    setGenPdf(true);
    try {
      const r = await fetch(`${BASE}/besoins/${b.id}/jd.pdf?download=1`, { headers: authH() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Fiche_de_poste_${(b.titre || b.title || 'besoin').replace(/\s+/g, '_')}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { showNotification?.(`Erreur génération PDF : ${e.message}`, 'error'); } finally { setGenPdf(false); }
  };

  const sectTitle = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--brand)', margin: '26px 0 12px' };
  const ta = { width: '100%', boxSizing: 'border-box', background: 'var(--code-bg)', border: '1px solid var(--input-border)', borderRadius: '10px', padding: '12px 14px', fontFamily: 'inherit', fontSize: '13.5px', lineHeight: 1.6, color: 'var(--text-strong)', resize: 'vertical', outline: 'none' };
  const hint = { fontSize: '11.5px', color: 'var(--text-faint)', margin: '0 0 6px' };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '18px 0' }}>
      <style>{`@keyframes jdFlash{0%{box-shadow:0 0 0 0 rgba(226,35,26,.35)}100%{box-shadow:0 0 0 16px rgba(226,35,26,0)}}
        .jd-ta:focus{border-color:var(--brand)!important;}`}</style>
      {/* Feuille : hauteur maîtrisée — barre du haut fixe, corps qui défile en interne.
          PAS d'overflow:hidden ici (il rognerait l'onglet dossier à top négatif). */}
      <div className="ats-jd-sheet" style={{ width: '100%', maxWidth: '780px', maxHeight: 'calc(100vh - 210px)', display: 'flex', flexDirection: 'column', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '3px 16px 16px 16px', position: 'relative', animation: modified && !editing ? 'jdFlash .9s ease-out' : undefined }}>
        <div style={{ position: 'absolute', top: '-1px', left: '-1px', height: '20px', padding: '0 14px', background: 'var(--surface)', borderTop: '1px solid var(--border)', borderRight: '1px solid var(--border)', borderRadius: '3px 12px 0 0', transform: 'translateY(-100%)' }} />

        {/* EN-TÊTE — fixe. Deux lignes : ce qu'est la fiche (statut, qui l'a
            validée), puis ce qu'on en fait. Les actions sont GROUPÉES par
            intention — rédiger, diffuser — et une seule est principale : la
            décision qui attend (valider un brouillon). Le panneau ouvert se lit
            sur son bouton, souligné de l'accent, comme un onglet. */}
        {/* Le sélecteur de fichier d'« Importer » vit HORS de la barre : la
            fiche vide n'a pas de barre, et son bouton « Importer » s'en sert. */}
        <input ref={fichierRef} type="file" accept=".pdf,.doc,.docx,.odt,.txt,.md" hidden
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; importer(f); }} />
        <div className="jd-tete">
          <div className="jd-tete__ligne">
            {/* PAS de titre « Fiche de poste » : l'onglet le dit déjà, et ce
                libellé passait pour le nom de ce qui s'ouvre dessous (« Rédiger
                avec Leman »). La ligne dit l'ÉTAT de la fiche, rien d'autre. */}
            {etat && <span className={`jd-statut jd-statut--${etat.cle}`}>{etat.libelle}</span>}
            {etat?.detail && <span className="jd-tete__meta">{etat.detail}</span>}
            {modified && <span className="jd-tete__meta" title={eff.updated_at ? `Dernière modification : ${fmtDate(eff.updated_at)}` : undefined}>modifiée</span>}
            {/* La décision qui attend, à côté de l'état qu'elle change. */}
            {!editing && !validated && jd && !vide && (
              <button type="button" onClick={validate} disabled={validating} className="erp-btn erp-btn--primary jd-tete__valider"
                title="Valider la fiche — elle peut partir, et alimente « JD validée » de Prêt à envoyer">
                <BI icon={validating ? 'arrow-repeat' : 'check2-circle'} className={validating ? 'jd-tourne' : undefined} /> Valider la fiche
              </button>
            )}
          </div>
          {/* Fiche vide : l'invitation du corps propose d'écrire, d'importer ou
              de faire rédiger — et il n'y a rien à envoyer. Pas de barre. */}
          {(editing || !vide) && <div className="jd-barre" role="toolbar" aria-label="Actions sur la fiche de poste">
            {editing ? (<>
              <span className="jd-barre__note">Une ligne par mission, une ligne par point du profil.</span>
              <button type="button" onClick={() => setEditing(false)} disabled={saving} className="erp-btn erp-btn--secondary jd-barre__fin">Annuler</button>
              <button type="button" onClick={save} disabled={saving} className="erp-btn erp-btn--primary">
                <BI icon={saving ? 'arrow-repeat' : 'check-lg'} className={saving ? 'jd-tourne' : undefined} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>) : (<>
              <div className="jd-seg" role="group" aria-label="Rédiger">
                <button type="button" className="jd-seg__btn" onClick={startEdit}><BI icon="pencil" /> Éditer</button>
                {jd && (
                  <button type="button" className="jd-seg__btn" onClick={() => basculer('leman')} aria-expanded={lemanOpen}
                    title="Leman écrit un brouillon d'après une fiche de poste déjà validée — vous le relisez et le validez">
                    <BI icon="file-earmark-text" /> Rédiger avec Leman
                  </button>
                )}
                <button type="button" className="jd-seg__btn" onClick={() => fichierRef.current?.click()} disabled={important}
                  title="Remplacer cette fiche de poste par une annonce (PDF, Word ou texte)">
                  <BI icon={important ? 'arrow-repeat' : 'upload'} className={important ? 'jd-tourne' : undefined} /> {important ? 'Import…' : 'Importer'}
                </button>
              </div>
              <div className="jd-seg" role="group" aria-label="Diffuser">
                <button type="button" className="jd-seg__btn" onClick={() => basculer('mail')} aria-expanded={mailOpen} title="Envoyer la fiche de poste par mail">
                  <BI icon="envelope" /> Envoyer
                </button>
                <button type="button" className="jd-seg__btn" onClick={() => basculer('partage')} aria-expanded={shareOpen} title="Liens publics de la fiche de poste">
                  <BI icon="link-45deg" /> Partager
                </button>
                <button type="button" className="jd-seg__btn" onClick={generatePdf} disabled={genPdf} title="Télécharger la fiche de poste en PDF">
                  <BI icon={genPdf ? 'arrow-repeat' : 'file-earmark-pdf'} className={genPdf ? 'jd-tourne' : undefined} /> {genPdf ? 'PDF…' : 'PDF'}
                </button>
              </div>
            </>)}
          </div>}
        </div>

        {/* LE RETOUR EN ARRIÈRE, tant qu'il est possible. Un import qui remplace
            la fiche de poste ne laisse aucune trace visible de ce qu'il a
            effacé : le serveur a rendu la version d'avant, on la garde à
            portée de clic plutôt que de renvoyer à une sauvegarde. */}
        {annule && !editing && (
          <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
            padding: '10px clamp(28px,4vw,52px)', borderBottom: '1px solid var(--hair)',
            background: 'var(--erp-warning-soft)' }}>
            <BI icon="clock-history" style={{ color: 'var(--erp-warning)' }} />
            <span style={{ flex: 1, fontSize: '12.5px', color: 'var(--text-2)' }}>
              Fiche de poste remplacée {annule.par === 'leman' ? 'par le brouillon de Leman' : "par l'import"}. La version précédente est encore récupérable.
            </span>
            <button onClick={revenir} disabled={important} className="ats-btn ats-btn-ghost"
              style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 700 }}>
              <BI icon="arrow-counterclockwise" /> Revenir à l'ancienne
            </button>
            <button onClick={() => setAnnule(null)} className="ats-btn ats-btn-ghost"
              title="Garder la version importée" style={{ padding: '6px 10px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600 }}>
              Garder
            </button>
          </div>
        )}

        {/* LA GRILLE D'ADJUDICATION (appels d'offres) : celle qui s'applique au
            score, d'où elle vient, ce qui n'a pas été compris — et sa
            validation. Rien ne s'affiche pour un poste sans grille ni alerte. */}
        {!editing && b.id != null && <GrilleBesoin besoinId={b.id} showNotification={showNotification} ouvrir={grilleOuvrir} />}

        {/* RÉDIGER AVEC LEMAN — le modèle se CHOISIT : celui du même client
            reprend le contexte, celui d'un autre client ne prête que missions
            et profil (le serveur le dit sur chaque ligne). */}
        {lemanOpen && !editing && (
          <div className="jd-panneau jd-leman">
            <div className="jd-panneau__tete">
              <span className="jd-panneau__titre">Rédiger avec Leman</span>
              <button type="button" className="jd-panneau__fermer" onClick={() => setPanneau(null)} aria-label="Fermer"><BI icon="x-lg" /></button>
            </div>
            <p className="jd-leman__aide">
              Leman écrit un brouillon d'après une fiche de poste déjà validée, du même client d'abord. Ce qu'il ne peut pas savoir, il le liste au lieu de l'inventer.
              {' '}Rien ne part avant votre validation : ni sourcing automatique, ni annonce, ni LinkedIn.
            </p>
            {modeles === null && <span className="jd-leman__etat"><BI icon="arrow-repeat" className="jd-tourne" /> Recherche des postes similaires…</span>}
            {modeles?.absent && <span className="jd-leman__etat">La rédaction par Leman arrive côté serveur.</span>}
            {modeles?.erreur && <span className="jd-leman__etat jd-leman__etat--erreur">{modeles.erreur}</span>}
            {Array.isArray(modeles) && (
              <div className="jd-leman__modeles" role="radiogroup" aria-label="Modèle de fiche de poste">
                {modeles.map(m => (
                  <label key={m.id} className={`jd-leman__modele${choix === m.id ? ' jd-leman__modele--choisi' : ''}`}>
                    <input type="radio" name={`jd-modele-${b.id}`} checked={choix === m.id} onChange={() => setChoix(m.id)} />
                    <span className="jd-leman__id">#{m.id}</span>
                    <span className="jd-leman__nom" title={`${m.titre}${m.client ? ` · ${m.client}` : ''}`}>{m.titre}{m.client ? ` · ${m.client}` : ''}</span>
                    <span className={`jd-leman__portee${m.meme_client ? ' jd-leman__portee--client' : ''}`}>
                      {m.meme_client ? 'même client' : 'autre client : missions et profil'}
                    </span>
                    {m.cree_le && <span className="jd-leman__date">{fmtDate(m.cree_le)}</span>}
                  </label>
                ))}
                <label className={`jd-leman__modele${choix === 'aucun' ? ' jd-leman__modele--choisi' : ''}`}>
                  <input type="radio" name={`jd-modele-${b.id}`} checked={choix === 'aucun'} onChange={() => setChoix('aucun')} />
                  <span className="jd-leman__nom">Sans modèle — à partir de l'intitulé{modeles.length ? '' : ' (aucun poste similaire dans vos besoins)'}</span>
                </label>
              </div>
            )}
            {!modeles?.absent && (<>
              <textarea value={consignes} onChange={e => setConsignes(e.target.value)} rows={2} maxLength={1500}
                placeholder="Précisions pour Leman (facultatif) : taille de l'équipe, méthode, jours sur site…"
                className="erp-field jd-leman__consignes" />
              <div className="jd-leman__actions">
                <label className="jd-leman__client">
                  <input type="checkbox" checked={avecClient} onChange={e => setAvecClient(e.target.checked)} />
                  Nommer le client et son secteur
                  <span className="jd-leman__client-aide">{avecClient
                    ? `${b.client || 'Le client'} apparaîtra dans la fiche, donc chez les candidats.`
                    : 'Par défaut, la fiche ne nomme ni le client ni son secteur.'}</span>
                </label>
                <button type="button" onClick={() => rediger(false)} disabled={redaction || modeles === null || !!modeles?.erreur}
                  className="erp-btn erp-btn--primary">
                  <BI icon={redaction ? 'arrow-repeat' : 'file-earmark-text'} className={redaction ? 'jd-tourne' : undefined} />
                  {redaction ? 'Leman rédige…' : 'Rédiger le brouillon'}
                </button>
              </div>
            </>)}
          </div>
        )}

        {/* D'OÙ VIENT CE TEXTE — un brouillon de Leman se lit comme tel tant
            qu'une personne ne l'a pas validé, et ce qui attend la validation
            (offre LinkedIn demandée) est nommé. */}
        {brouillonLeman && !editing && (
          <div className="jd-provenance">
            <span className="jd-provenance__icone"><BI icon="file-earmark-text" /></span>
            <div className="jd-provenance__texte">
              <div className="jd-provenance__ligne">
                <strong>Brouillon de Leman</strong>
                {jd.modele ? (
                  <span className="jd-provenance__modele" title={jd.source === 'ia_modele_autre_client' ? "Modèle d'un autre client : seuls les missions et le profil en sont repris" : undefined}>
                    d'après <span className="jd-provenance__id">#{jd.modele.id}</span> {jd.modele.titre}{jd.modele.client ? ` · ${jd.modele.client}` : ''}
                    {jd.source === 'ia_modele_autre_client' && ' — missions et profil'}
                  </span>
                ) : <span className="jd-provenance__modele">à partir de l'intitulé, sans modèle</span>}
                {jd.anonyme && <span className="jd-provenance__attente"><BI icon="eye-slash" /> sans client ni secteur</span>}
                {(jd.en_attente || []).includes('linkedin') && <span className="jd-provenance__attente"><BI icon="linkedin" /> LinkedIn part à la validation</span>}
              </div>
              <div className="jd-provenance__aide">Relisez-le, corrigez-le si besoin, puis validez-le : ni recherche, ni annonce, ni LinkedIn ne partent avant.</div>
            </div>
          </div>
        )}

        {/* ENVOI PAR MAIL — le lien part avec le message, le PDF en pièce jointe.
            Le choix « client masqué » est POSÉ ICI, avant d'envoyer : c'est la
            seule décision de ce formulaire qu'on ne peut pas reprendre. */}
        {mailOpen && (
          <div className="jd-panneau">
            <div className="jd-panneau__tete">
              <span className="jd-panneau__titre">Envoyer la fiche</span>
              <button type="button" className="jd-panneau__fermer" onClick={() => setPanneau(null)} aria-label="Fermer"><BI icon="x-lg" /></button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-2)', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={mailAnon} onChange={e => setMailAnon(e.target.checked)} style={{ accentColor: 'var(--brand)' }} />
                Client masqué
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-2)', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={mailPdf} onChange={e => setMailPdf(e.target.checked)} style={{ accentColor: 'var(--brand)' }} />
                Joindre le PDF
              </label>
              <div style={{ flex: 1 }} />
              <button type="button" onClick={envoyerParMail} disabled={mailBusy || !mailTo.trim()} className="erp-btn erp-btn--primary erp-btn--sm">
                <BI icon={mailBusy ? 'arrow-repeat' : 'send'} className={mailBusy ? 'jd-tourne' : undefined} /> Envoyer
              </button>
            </div>
            <input value={mailTo} onChange={e => setMailTo(e.target.value)} autoFocus
              placeholder="marc@exemple.ch, ana@client.ch"
              className="erp-field" style={{ width: '100%', boxSizing: 'border-box', fontSize: '12.5px' }} />
            <textarea value={mailMot} onChange={e => setMailMot(e.target.value)} rows={2}
              placeholder="Un mot d'introduction (facultatif)"
              className="erp-field" style={{ width: '100%', boxSizing: 'border-box', fontSize: '12.5px', resize: 'vertical' }} />
            <div className="jd-panneau__aide">
              {mailAnon
                ? 'Le nom du client n\'apparaîtra ni dans le message, ni dans le PDF, ni sur la page en ligne.'
                : `Le nom du client${b.client ? ` (${b.client})` : ''} sera visible par les destinataires.`}
              {' '}Le lien envoyé reste à jour : la fiche y est toujours dans sa dernière version.
            </div>
          </div>
        )}

        {/* Panneau de partage — liens publics /jd/:token */}
        {shareOpen && (
          <div className="jd-panneau">
            <div className="jd-panneau__tete">
              <span className="jd-panneau__titre">Liens publics</span>
              <button type="button" className="jd-panneau__fermer" onClick={() => setPanneau(null)} aria-label="Fermer"><BI icon="x-lg" /></button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-2)', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={shareAnon} onChange={e => setShareAnon(e.target.checked)} style={{ accentColor: 'var(--brand)' }} />
                Anonyme (client masqué)
              </label>
              <div style={{ flex: 1 }} />
              <button type="button" onClick={createShare} disabled={shareBusy} className="erp-btn erp-btn--primary erp-btn--sm">
                <BI icon={shareBusy ? 'arrow-repeat' : 'link-45deg'} className={shareBusy ? 'jd-tourne' : undefined} /> Créer un lien
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {shares === null && <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</span>}
              {Array.isArray(shares) && shares.length === 0 && <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Aucun lien actif — créez-en un pour partager la fiche.</span>}
              {Array.isArray(shares) && shares.map((s, i) => {
                const u = shareUrlOf(s);
                return (
                  <div key={s.token ?? i} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', background: s.anonyme ? 'var(--accent-soft-bg)' : 'var(--surface)', color: 'var(--text-2)', border: '1px solid var(--hair)', flex: 'none' }}>{s.anonyme ? 'Anonyme' : 'Complet'}</span>
                    <span title={u} style={{ flex: 1, minWidth: '160px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u}</span>
                    <span title="Nombre d'ouvertures" style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-3)', flex: 'none' }}><BI icon="eye" style={{ fontSize: '10px', marginRight: '3px' }} />{s.views ?? 0}</span>
                    <button onClick={() => copyLink(u)} title="Copier le lien" className="ats-btn ats-btn-ghost" style={{ padding: '4px 9px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 600 }}><BI icon="clipboard" /></button>
                    <button onClick={() => revokeShare(s)} title="Révoquer ce lien" className="ats-btn ats-btn-ghost" style={{ padding: '4px 9px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 600, color: 'var(--danger-fg)' }}><BI icon="trash3" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Corps — défile en interne, coins bas arrondis (c'est LUI qui clippe) */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', borderRadius: '0 0 16px 16px', padding: '18px clamp(28px,4vw,52px) clamp(24px,3vw,36px)' }}>
          {/* Titre */}
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: 'clamp(24px,3vw,32px)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', lineHeight: 1.1 }}>{b.titre || b.title || 'Poste'}<span style={{ color: 'var(--brand)' }}>.</span></div>
          {(b.client || b.ville) && <div style={{ marginTop: '5px', fontSize: '13.5px', color: 'var(--text-2)' }}>{[b.client, b.ville].filter(Boolean).join(' · ')}</div>}

          {/* Bande méta (appartient au besoin — non éditable ici) */}
          <div className="jd-meta">
            {meta.map(([k, v], i) => (
              <div key={i} style={{ background: 'var(--surface)', padding: '11px 13px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>{k}</div>
                <div style={{ marginTop: '3px', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', fontFamily: "'IBM Plex Mono',monospace" }}>{v}</div>
              </div>
            ))}
          </div>

          {loading && !jd ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}><BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement de la fiche…</div>
          ) : editing ? (
            <div style={{ marginTop: '22px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <div style={sectTitle}>Contexte</div>
                <textarea className="jd-ta" value={draft.contexte} onChange={e => setDraft(d => ({ ...d, contexte: e.target.value }))} rows={4} style={ta} />
              </div>
              <div>
                <div style={sectTitle}>Missions principales</div>
                <p style={hint}>Une ligne = une mission.</p>
                <textarea className="jd-ta" value={draft.missions} onChange={e => setDraft(d => ({ ...d, missions: e.target.value }))} rows={6} style={ta} />
              </div>
              <div>
                <div style={sectTitle}>Profil recherché</div>
                <p style={hint}>Une ligne = un point du profil.</p>
                <textarea className="jd-ta" value={draft.profil} onChange={e => setDraft(d => ({ ...d, profil: e.target.value }))} rows={5} style={ta} />
              </div>
            </div>
          ) : vide ? (
            // Pas encore de fiche : l'état vide propose les trois façons d'en avoir une.
            <div className="jd-vide">
              <div className="jd-vide__titre">Ce besoin n'a pas encore de fiche de poste.</div>
              <p>Écrivez-la, importez une annonce, ou laissez Leman rédiger un brouillon d'après un poste similaire déjà validé — vous le relisez et le validez avant qu'il ne parte.</p>
              <div className="jd-vide__actions">
                <button type="button" className="erp-btn erp-btn--primary" onClick={() => setPanneau('leman')}><BI icon="file-earmark-text" /> Rédiger avec Leman</button>
                <button type="button" className="erp-btn erp-btn--secondary" onClick={startEdit}><BI icon="pencil" /> Écrire</button>
                <button type="button" className="erp-btn erp-btn--secondary" onClick={() => fichierRef.current?.click()} disabled={important}><BI icon="upload" /> Importer</button>
              </div>
            </div>
          ) : (<>
            {/* RELECTURE avant validation — des RÈGLES (sections, longueur, restes
                d'une ancienne mission, rémunération, critères à risque, trous),
                pas un avis : elles signalent, la personne décide. */}
            {!validated && points.length > 0 && (
              <div className="jd-relecture">
                <div className="jd-relecture__tete">
                  Avant de valider
                  <span className={`jd-relecture__compte${aRelire.length ? '' : ' jd-relecture__compte--ok'}`}>
                    {aRelire.length ? `${aRelire.length} à relire` : 'rien à signaler'}
                  </span>
                  <span className="jd-relecture__regle">contrôles automatiques — ils signalent, vous décidez</span>
                </div>
                <ul>
                  {aRelire.map(p => (
                    <li key={p.cle} className="jd-relecture__attention">
                      <BI icon="exclamation-triangle" />
                      {p.cle === 'a_verifier' && (jd.a_verifier || []).length ? (
                        <span>Leman ne pouvait pas savoir :
                          <ul className="jd-relecture__sous">{jd.a_verifier.map((x, i) => <li key={i}>{x}</li>)}</ul>
                        </span>
                      ) : <span>{p.message}</span>}
                    </li>
                  ))}
                  {infos.map(p => <li key={p.cle} className="jd-relecture__info"><BI icon="info-circle" /><span>{p.message}</span></li>)}
                  {sansRemarque.length > 0 && (
                    <li className="jd-relecture__ok" title={sansRemarque.map(p => p.message).join('\n')}>
                      <BI icon="check2" /><span>{sansRemarque.length} contrôle{sansRemarque.length > 1 ? 's' : ''} sans remarque</span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Contexte — prose */}
            {eff.contexte && (<>
              <div style={sectTitle}>Contexte</div>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.7, color: 'var(--text)', maxWidth: '64ch', textWrap: 'pretty' }}>{eff.contexte}</p>
            </>)}

            {/* Missions — lignes numérotées 01 02 03 */}
            {(eff.missions || []).length > 0 && (<>
              <div style={sectTitle}>Missions principales</div>
              <div>
                {eff.missions.map((m, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '13px', padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', fontWeight: 600, color: 'var(--text-faint)', flex: 'none', paddingTop: '2px' }}>{String(i + 1).padStart(2, '0')}</span>
                    <span style={{ fontSize: '14px', lineHeight: 1.55, color: 'var(--text)' }}>{m}</span>
                  </div>
                ))}
              </div>
            </>)}

            {/* Compétences (du besoin — non éditables ici) */}
            {skills.length > 0 && (<>
              <div style={sectTitle}>Compétences requises</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {skills.map((s, i) => <span key={i} style={{ fontSize: '12px', fontWeight: 500, color: 'var(--chip-fg, var(--text-2))', background: 'var(--chip-bg)', padding: '4px 10px', borderRadius: '6px' }}>{s}</span>)}
              </div>
            </>)}

            {/* Profil — carrés rouges */}
            {(eff.profil || []).length > 0 && (<>
              <div style={sectTitle}>Profil recherché</div>
              <div>
                {eff.profil.map((l, i) => estRubrique(l) ? (
                  <div key={i} style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', padding: i ? '12px 0 2px' : '2px 0 2px' }}>{libelleRubrique(l)}</div>
                ) : (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '5px 0' }}>
                    <span style={{ width: '5px', height: '5px', background: 'var(--brand)', marginTop: '8px', flex: 'none' }} />
                    <span style={{ fontSize: '14px', lineHeight: 1.5, color: 'var(--text)' }}>{l}</span>
                  </div>
                ))}
              </div>
            </>)}
          </>)}

          {/* Pied — signature du document */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '32px', paddingTop: '16px', borderTop: '1px solid var(--hair)', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-faint)' }}>
            <span>{b.reference || b.ref || `#${b.id}`} · créé le {fmtDate(b.created_at || b.createdAt)}{(b.echeance || b.deadline) ? ` · échéance ${fmtDate(b.echeance || b.deadline)}` : ''}</span>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ display: 'inline-flex', width: '11px', height: '11px' }}><svg viewBox="0 0 32 32" style={{ width: '100%', height: '100%' }}><rect width="32" height="32" rx="6" fill="#E2231A" /><path d="M13.5 6.5H18.5V13.5H25.5V18.5H18.5V25.5H13.5V18.5H6.5V13.5H13.5Z" fill="#fff" /></svg></span>
              erpia
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
